import type { Vec2 } from "../../core/vec";
import type { MissionBuild } from "../mission";
import { pieceForward } from "../network";
import { compileMap } from "./compile";
import { actorKind, PROP_KINDS } from "./registry";
import type { MapDoc, MapIssue } from "./types";

/** Conservative ground-level footprint, not the foliage/roof/overhead span. */
export const DECOR_GROUND_RADIUS: Readonly<Record<string, number>> = {
  tree: 0.25, planter: 0.35, stall: 0.6, food_shop: 1, apartment: 1.5,
  shrine: 0.5, customer: 0.25, sign: 0.1,
};

/** Use the compiler's layout frame, including its pre-delivery-snap convention. */
export function actorWorldPaths(doc: MapDoc): Vec2[][] {
  const groups = (doc.actors ?? []).map(a => [ ...(a.at ? [a.at] : []), ...(a.path ?? []), ...(a.to ? [a.to] : []) ]);
  const flat = groups.flat();
  if (!flat.length) return groups.map(() => []);
  const resolved = compileMap({ ...doc, decor: flat.map(at => ({ kind: "sign", at })) }, { level: "mission", variant: "A", mirror: false, seed: 1 }).decor;
  let index = 0;
  return groups.map(g => g.map(() => { const p = resolved[index++]!; return { x: p.x, z: p.z }; }));
}

/** Bounded authoring checks. These do not prove traffic choreography or finishability. */
export function checkMapRealism(doc: MapDoc, build: MissionBuild): MapIssue[] {
  const out: MapIssue[] = [];
  const warn = (code: string, path: string, message: string, fix: string, at?: Vec2): void => {
    out.push({ level: "warning", code, path, message, fix, ...(at ? { at: [at.x, at.z] as [number, number] } : {}) });
  };
  const { network } = build;
  const paths = actorWorldPaths(doc);
  const agents = build.makeAgents().map(a => a.state);
  const nearbyContext = (p: Vec2): boolean => build.obstacles.some(o =>
    ["traffic_cone", "barrier", "parked_car", "broken_down_truck"].includes(o.kind ?? "") && Math.hypot(o.x-p.x,o.z-p.z) < 9) ||
    agents.some(a => ["standing_person", "vendor_cart"].includes(a.type) && Math.hypot(a.x-p.x,a.z-p.z) < 9);
  build.decor.forEach((d,i) => {
    const radius = DECOR_GROUND_RADIUS[d.kind];
    if (radius !== undefined && network.sdf(d.x,d.z) < radius - 0.05) warn("MAP_DECOR_ROAD", `decor[${i}].at`,
      `${d.kind}'s ground footprint intrudes into drivable road but has no collision`, "Place the scenery beyond the kerb; use a solid prop for an intended road obstruction.", d);
  });
  build.obstacles.forEach((o,i) => {
    const expected = PROP_KINDS[o.kind ?? ""];
    if (expected) {
      const ratios = [Math.min(o.halfW,o.halfD)/Math.min(expected.halfW,expected.halfD), Math.max(o.halfW,o.halfD)/Math.max(expected.halfW,expected.halfD), o.height/expected.height];
      if (ratios.some(r=>r<0.25||r>3)) warn("MAP_PROP_SCALE", `props[${i}]`, `${o.kind} dimensions are far outside its registered physical size`, "Check metre units and intended object kind; do not rescale a scenario obstacle automatically.", o);
    }
    const piece = network.pieces[network.deepestPiece(o.x,o.z)];
    if (!piece) return;
    const theta = pieceForward(piece,o.x,o.z);
    const side = o.halfW*Math.abs(Math.cos(theta)) + o.halfD*Math.abs(Math.sin(theta));
    const depth = -network.sdf(o.x,o.z);
    if (o.kind === "broken_down_truck" && depth > 0.2 && (o.halfW>o.halfD) !== (Math.abs(Math.sin(theta))>=Math.abs(Math.cos(theta)))) {
      warn("MAP_VEHICLE_BROADSIDE", `props[${i}]`, "this broken_down_truck lies broadside across the carriageway",
        "If it is backing out, use a cued reverse vehicle actor and a real bay. If disabled, align it with the lane and stage a breakdown context.", o);
    }
    if (["parked_car", "parked_motorbike", "broken_down_truck"].includes(o.kind ?? "") && depth > side+0.65) {
      warn("MAP_VEHICLE_LIVE_LANE", `props[${i}].at`, `${o.kind} is stationary in a live lane, ${depth.toFixed(1)} m inside the kerb`,
        nearbyContext(o) ? "Review the explicit breakdown/loading scene and remaining passing space; a role alone does not make this ordinary parking." : "Move ordinary parking beside the same kerb, or deliberately author a visible breakdown/loading scene.", o);
    }
    if (["sofa", "mattress", "ladder", "spilled_crate", "water_tank", "pallet"].includes(o.kind ?? "") && depth > side+0.5 && !nearbyContext(o)) {
      warn("MAP_OBJECT_CONTEXT", `props[${i}]`, `${o.kind} is isolated in the live roadway without nearby loading, work or spill context`,
        "Stage a plausible source and action, or move this ordinary storage item to the kerb. Do not add a blanket blocking role.", o);
    }
  });
  (doc.actors ?? []).forEach((a,i) => {
    const kind = actorKind(a.type)!;
    const pts = paths[i]!;
    const p = `actors[${i}]`;
    const moving = (a.speed ?? kind.speed) > 0;
    const length = pts.slice(1).reduce((sum,b,k) => sum + Math.hypot(b.x-pts[k]!.x,b.z-pts[k]!.z),0);
    if (["vehicle", "walker"].includes(kind.backing) && moving && length < 0.1) warn("MAP_ACTOR_ZERO_PATH", `${p}.path`, `${a.id} has movement speed but no nonzero path`, "Give it a real movement path or explicitly author a stationary actor.", pts[0]);
    if (a.cueDelayS !== undefined && kind.backing !== "vehicle") warn("MAP_CUE_IGNORED", `${p}.cueDelayS`, `${a.type} does not consume cueDelayS`, "Walkers cue on trigger; pets use their fixed cue sequence. Remove the ignored setting and verify the actual cue behavior.");
    if (a.role === "reversing" || a.motion === "reverse") {
      if (kind.backing !== "vehicle" || a.motion !== "reverse" || a.role !== "reversing" || !moving || (a.speed ?? kind.speed) > 1.5 || (a.cueDelayS ?? 0) < 0.8 || length < 0.1 || a.canOvertake === true || a.parkAtEnd !== true) {
        warn("MAP_REVERSE_CONTRACT", p, `${a.id}'s reversing intent does not match its action and cue settings`, 'Use a vehicle with motion:"reverse", role:"reversing", a nonzero path, 0 < speed <= 1.5, cueDelayS >= 0.8, parkAtEnd:true and no overtaking.');
      }
    }
    if (kind.backing !== "vehicle") return;
    if (a.triggerAt != null && (a.cueDelayS ?? 0) < 0.8 && pts[0]) {
      const robot = build.path.point(Math.min(a.triggerAt, build.path.length));
      const state = agents[i]!;
      if (Math.hypot(state.x-robot.x,state.z-robot.z)<12) warn("MAP_CUE_NEAR", `${p}.cueDelayS`, `${a.id} wakes within 12 m without 0.8 s of advance cue`, "Add a readable cue delay or deliberately choreograph its trigger farther from the robot.", state);
    }
    for (let k=0;k+1<pts.length;k++) {
      const start=pts[k]!, end=pts[k+1]!;
      const n=Math.max(1,Math.ceil(Math.hypot(end.x-start.x,end.z-start.z)));
      let bad: Vec2|undefined;
      for(let j=1;j<n;j++) { const t=j/n; const q={x:start.x+(end.x-start.x)*t,z:start.z+(end.z-start.z)*t}; if(network.sdf(q.x,q.z)>0.6 && j>1 && j<n-1) {bad=q;break;} }
      if(bad) { warn("MAP_ACTOR_PATH_OFFROAD", `${p}.path[${k}]`, `${a.id}'s segment leaves drivable ground between its waypoints`, "Trace the path along existing road or a real access bay; endpoint checks alone miss this cut-through.",bad); break; }
    }
    const stopped = !moving ? agents[i] : a.parkAtEnd ? pts[pts.length-1] : undefined;
    if(stopped && -network.sdf(stopped.x,stopped.z)>agents[i]!.radius+0.7) warn("MAP_ACTOR_PARK_LANE", a.parkAtEnd ? `${p}.path[${pts.length-1}]` : `${p}.speed`, `${a.id} stays stopped in a live traffic lane`, "End at a kerb/bay, leave the scene, or explicitly stage and review the obstruction.", stopped);
  });
  // Actual compiled idle body circles, including automatic kerb pull-out positions.
  agents.forEach((a,i) => {
    if(!a.active || !a.collidable) return;
    const circles = a.body.map(d=>({x:a.x+Math.sin(a.heading)*d,z:a.z+Math.cos(a.heading)*d}));
    const hit = build.obstacles.find(o=>!o.bump && circles.some(c=>Math.hypot(Math.max(0,Math.abs(c.x-o.x)-o.halfW),Math.max(0,Math.abs(c.z-o.z)-o.halfD))<a.radius-0.05));
    for(let j=i+1;j<agents.length;j++) {
      const b=agents[j]!;
      if(!b.active || !b.collidable) continue;
      if(circles.some(c=>b.body.some(d=>Math.hypot(c.x-b.x-Math.sin(b.heading)*d,c.z-b.z-Math.cos(b.heading)*d)<a.radius+b.radius-0.05))) warn("MAP_ACTOR_IDLE_OVERLAP", `actors[${j}]`, `${b.id}'s compiled waiting body overlaps ${a.id}`, "Place a real queue with body clearance; different trigger times do not separate waiting bodies.", b);
    }
    if(hit) warn("MAP_ACTOR_IDLE_PROP", `actors[${i}]`, `${a.id}'s compiled idle body intersects ${hit.kind}`, "Separate the waiting actor from the solid prop; triggerAt delays movement, not physical presence.", a);
  });
  return out;
}
